import { Fragment, useMemo, useState } from "react";
import { FollowersByCountry } from "../../types";
import CountriesPresenter from "./CountriesPresenter";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ConnectionName } from "@arelia-types/connection";
import useRequiredConnections from "@arelia-utils/helpers/useRequiredConnections";
import { useFirstRecords, useTopCountries } from "../../api/streaming";
import { useFollowerArtistFilter } from "@arelia-hooks/artistAccount";
import { TopCountriesPeriod } from "./models";
import WidgetMenuSelector from "../../components/ui-atoms/WidgetMenuSelector";
import { Box } from "@mui/material";
import { ArelTooltip } from "../../components/ui-atoms/Tooltip";
import theme from "../../theme";

export default function CountriesContainer() {
  const requiredConnectionNames = [ConnectionName.youtube, ConnectionName.spotify, ConnectionName.amazon, ConnectionName.appleMusic];
  const hasRequiredConnections = useRequiredConnections(requiredConnectionNames);
  const isSpotifyConnected = useRequiredConnections([ConnectionName.spotify]);
  const [selectedPeriod, setSelectedPeriod] = useState(TopCountriesPeriod.AllTime);
  const { data: topCountries = [], isLoading: isCountriesLoading, isError: isCountriesError } = useTopCountries(selectedPeriod, { enabled: hasRequiredConnections });
  const { data: firstRecords = [], isLoading: isRecordsLoading, isError: isRecordsError } = useFirstRecords({ enabled: selectedPeriod === TopCountriesPeriod.AllTime });
  const filteredData = useFollowerArtistFilter(topCountries);

  const countries = useMemo<FollowersByCountry[]>(() => {
    return filteredData.map((f) => {
      return {
        networkName: f.networkName,
        byCountry: f.byCountry || {},
        totalByCountry: (f.byCountry && Object.values(f.byCountry).reduce((acc, country) => acc + country, 0)) || 0,
      }
    })
  }, [filteredData]);

  const menuItems = [
    { label: '28 days', value: TopCountriesPeriod.TwentyEightDays },
    { label: '1 year', value: TopCountriesPeriod.OneYear },
    { label: 'All', value: TopCountriesPeriod.AllTime }
  ];

  const renderPeriodSelector = () => (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }}>
      <WidgetMenuSelector<TopCountriesPeriod>
        onClick={setSelectedPeriod}
        selectedValue={selectedPeriod}
        menuItems={menuItems}
      />
      {
        isSpotifyConnected && selectedPeriod === TopCountriesPeriod.OneYear
          ? <ArelTooltip title="Data not available for Spotify">
            <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main, cursor: "pointer" }} />
          </ArelTooltip>
          : <></>
      }
      {
        hasRequiredConnections && firstRecords.length > 0 && selectedPeriod === TopCountriesPeriod.AllTime
          ? <ArelTooltip title={
            firstRecords
              .map((x, i) =>
                <div key={i}>
                  {x.networkName}: {x.failReason ? 'Failed to get data' : `since ${new Date(x.record.publishedAt).toLocaleDateString()}`}
                </div>)
              .concat([isSpotifyConnected
                ? <Fragment key={99}>
                  Spotify: Not Available
                </Fragment>
                : <></>
              ])
          }>
            <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main, cursor: "pointer" }} />
          </ArelTooltip>
          : <></>
      }
    </Box>

  );

  return (
    <CountriesPresenter
      countries={countries}
      loading={isCountriesLoading || isRecordsLoading}
      error={isCountriesError && isRecordsError}
      unconnected={!hasRequiredConnections}
      id="STREAMING_COUNTRIES"
      headerComponent={renderPeriodSelector()}
    />
  );
}
